import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Card,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
  Radio,
  Tooltip,
  Badge,
  Tag,
  Spin,
  Empty,
  Tabs,
  Alert,
  Input,
  theme,
} from 'antd';
import {
  FileTextOutlined,
  CompressOutlined,
  BarChartOutlined,
  DownloadOutlined,
  EyeOutlined,
  SettingOutlined,
  PlusOutlined,
  StarFilled,
  GlobalOutlined,
  BuildOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import PayslipTemplateBuilder, { hydratePayslipTemplate } from './PayslipTemplateBuilder';
import type { PayslipTemplate as PayslipTemplateConfig } from './PayslipTemplateBuilder';
import { useSelection } from '../../SelectionContext';
import { getAuthorizedCompanyContext } from '../../utils/companyContext';
import API_CONFIG from '../../config/api';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface TemplateSelectorProps {
  visible: boolean;
  onCancel: () => void;
  payslipIds: number[];
  isBulk?: boolean;
  onDownloadComplete?: () => void;
}

interface PayslipTemplate extends PayslipTemplateConfig {
  id: number;
  is_global?: boolean;
  created_at: string;
  updated_at: string;
}

// Templates statiques de base (fallback)
const staticTemplateOptions = [
  {
    key: 'standard',
    name: 'Format standard',
    description: 'Présentation complète du bulletin enregistré',
    icon: <FileTextOutlined />,
    color: '#1890ff',
    features: [
      'Identité de l’employeur et du salarié',
      'Rubriques et totaux de la paie',
      'Format professionnel A4',
      'Signature et cachet employeur'
    ],
    recommended: true,
  },
  {
    key: 'compact',
    name: 'Format Compact',
    description: 'Version condensée pour économiser le papier',
    icon: <CompressOutlined />,
    color: '#52c41a',
    features: [
      'Informations essentielles',
      'Design optimisé',
      'Économie de papier',
      'Lecture rapide'
    ],
  },
  {
    key: 'detailed',
    name: 'Détaillé avec IUTS',
    description: 'Avec breakdown complet des tranches IUTS',
    icon: <BarChartOutlined />,
    color: '#722ed1',
    features: [
      'Détail complet tranches IUTS',
      'Séquence de calcul',
      'Exonérations détaillées',
      'Analyse fiscale complète'
    ],
  },
];

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  visible,
  onCancel,
  payslipIds,
  isBulk = false,
  onDownloadComplete,
}) => {
  const { token, hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const { token: themeToken } = theme.useToken();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('default');
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [templates, setTemplates] = useState<PayslipTemplate[]>([]);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | undefined>();
  const [previewHTML, setPreviewHTML] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copyTemplate, setCopyTemplate] = useState<PayslipTemplate | null>(null);
  const [copyName, setCopyName] = useState('');
  const [copying, setCopying] = useState(false);
  const requestVersion = useRef(0);
  const contextKey = `${token}:${selectedCompany}`;
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  const loadController = useRef<AbortController | null>(null);
  const canCreate = hasPermission('Ressources Humaines.Templates.Bulletins.create');
  const canEdit = hasPermission('Ressources Humaines.Templates.Bulletins.edit');
  const canCopy = hasPermission('Ressources Humaines.Templates.Bulletins.duplicate');
  const canPrint = hasPermission('Ressources Humaines.Paie.Bulletins.print');
  const canView = hasPermission('Ressources Humaines.Paie.Bulletins.view');
  const requestHeaders = () => {
    const companyId = selectedCompany ? getAuthorizedCompanyContext() : null;
    if (selectedCompany && !companyId) throw new Error('Sélectionnez une entreprise accessible pour charger les bulletins et leurs modèles.');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
      ...(companyId ? { 'X-Company-Id': String(companyId) } : {}) };
  };

  const loadTemplates = useCallback(async () => {
    if (!token || !visible) return;
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadError('');
    setTemplates([]);
    setSelectedTemplate('default');
    try {
      const response = await fetch(API_CONFIG.url('/api/hr/payslip-templates?include_global=true'), {
        headers: requestHeaders(), signal: controller.signal
      });
      const data = await response.json();
      if (version !== requestVersion.current || controller.signal.aborted) return;
      if (!response.ok || !data.success || !Array.isArray(data.data)) throw new Error(data.error || 'Impossible de charger les modèles');
      setTemplates(data.data.map((item: PayslipTemplate) => ({ ...item, ...hydratePayslipTemplate(item) })));
    } catch (error: any) {
      if (version === requestVersion.current && !controller.signal.aborted) setLoadError(error.message || 'Impossible de charger les modèles');
    } finally {
      if (version === requestVersion.current && !controller.signal.aborted) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, visible, selectedCompany]);

  useEffect(() => {
    setShowTemplateBuilder(false);
    setCopyTemplate(null);
    setPreviewOpen(false);
    setPreviewHTML('');
    setDownloading(false);
    setPreviewing(false);
    setCopying(false);
    if (visible) loadTemplates();
    return () => { requestVersion.current += 1; loadController.current?.abort(); };
  }, [visible, loadTemplates]);

  const getTemplateRequest = () => {
    const custom = templates.find(t => String(t.id) === selectedTemplate);
    return custom ? { template_id: custom.id } : { template_type: selectedTemplate };
  };

  const handleDownload = async () => {
    if (!canPrint || downloading || loading || !selectedTemplate || payslipIds.length === 0) return;
    const version = requestVersion.current;
    setDownloading(true);
    try {
      const selection = getTemplateRequest();
      const response = isBulk || payslipIds.length > 1
        ? await fetch(API_CONFIG.url('/api/hr/payslips/pdf/bulk'), {
          method: 'POST', headers: requestHeaders(), body: JSON.stringify({ payslip_ids: payslipIds, ...selection })
        })
        : await fetch(API_CONFIG.url(`/api/hr/payslips/${payslipIds[0]}/pdf/${'template_id' in selection ? 'standard' : selectedTemplate}${'template_id' in selection ? `?template_id=${selection.template_id}` : ''}`), {
          headers: requestHeaders()
        });
      if (version !== requestVersion.current) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la génération du PDF');
      }
      const blob = await response.blob();
      if (version !== requestVersion.current) return;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('content-disposition');
      const filenameMatch = contentDisposition?.match(/filename="([^"\r\n]+)"/);
      a.download = filenameMatch?.[1] || `bulletins_paie_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`${payslipIds.length} bulletin(s) téléchargé(s)`);
      onDownloadComplete?.();
      onCancel();
    } catch (error: any) {
      if (version === requestVersion.current) message.error(error.message || 'Erreur lors du téléchargement');
    } finally {
      if (version === requestVersion.current) setDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (!canView || previewing || loading || !payslipIds.length) return;
    const version = requestVersion.current;
    setPreviewing(true);
    try {
      const selection = getTemplateRequest();
      const query = 'template_id' in selection ? `template_id=${selection.template_id}` : `template_type=${selection.template_type}`;
      const response = await fetch(API_CONFIG.url(`/api/hr/payslips/${payslipIds[0]}/preview?${query}`), { headers: requestHeaders() });
      const data = await response.json();
      if (version !== requestVersion.current) return;
      if (!response.ok || !data.success || typeof data.data?.html !== 'string') throw new Error(data.error || 'Impossible de générer l’aperçu');
      setPreviewHTML(data.data.html);
      setPreviewOpen(true);
    } catch (error: any) {
      if (version === requestVersion.current) message.error(error.message || 'Erreur lors de l’aperçu');
    } finally {
      if (version === requestVersion.current) setPreviewing(false);
    }
  };

  const handleCopy = async () => {
    if (!canCopy || !copyTemplate || !copyName.trim() || copying) return;
    const version = requestVersion.current;
    const copyContext = currentContext.current;
    setCopying(true);
    try {
      const response = await fetch(API_CONFIG.url(`/api/hr/payslip-templates/${copyTemplate.id}/duplicate`), {
        method: 'POST', headers: requestHeaders(), body: JSON.stringify({ new_name: copyName.trim() })
      });
      const data = await response.json();
      if (version !== requestVersion.current) return;
      if (!response.ok || !data.success) throw new Error(data.error || 'Impossible de copier le modèle');
      setCopyTemplate(null);
      message.success('Modèle copié dans votre entreprise');
      await loadTemplates();
      if (canEdit && data.data?.id) { setEditingTemplateId(data.data.id); setShowTemplateBuilder(true); }
    } catch (error: any) {
      if (version === requestVersion.current) message.error(error.message || 'Erreur lors de la copie');
    } finally { if (copyContext === currentContext.current) setCopying(false); }
  };

  // Gestion des templates personnalisés
  const handleCreateTemplate = () => {
    if (!canCreate) return;
    setEditingTemplateId(undefined);
    setShowTemplateBuilder(true);
  };

  const handleEditTemplate = (templateId: number) => {
    if (!canEdit || templates.find(t => t.id === templateId)?.is_global) return;
    setEditingTemplateId(templateId);
    setShowTemplateBuilder(true);
  };

  const handleTemplateSaved = () => {
    setShowTemplateBuilder(false);
    setEditingTemplateId(undefined);
    loadTemplates(); // Recharger la liste
  };

  // Obtenir le template sélectionné ou un template statique
  const getSelectedTemplate = () => {
    if (selectedTemplate === 'default') return { name: 'Modèle par défaut de l’entreprise', description: 'Le modèle actif choisi par votre entreprise, ou le format standard si aucun modèle n’est défini.', color: themeToken.colorPrimary };
    return templates.find(t => String(t.id) === selectedTemplate) || staticTemplateOptions.find(t => t.key === selectedTemplate);
  };

  const selectedTemplateOption = getSelectedTemplate();

  return (
    <Modal
      title={
        <Space wrap>
          <FileTextOutlined style={{ color: themeToken.colorPrimary }} />
          <span>{payslipIds.length ? 'Choisir un modèle de bulletin' : 'Modèles de bulletin de l’entreprise'}</span>
          {payslipIds.length > 1 && (
            <Text type="secondary">({payslipIds.length} bulletins sélectionnés)</Text>
          )}
        </Space>
      }
      open={visible}
      onCancel={downloading ? undefined : onCancel}
      closable={!downloading}
      maskClosable={!downloading}
      width={900}
      footer={[
        <Button key="cancel" disabled={downloading} onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="create"
          icon={<PlusOutlined />}
          onClick={handleCreateTemplate}
          disabled={!canCreate || loading || downloading}
        >
          Créer un modèle
        </Button>,
        <Button
          key="preview"
          icon={<EyeOutlined />}
          onClick={handlePreview}
          loading={previewing}
          disabled={!canView || loading || downloading || !payslipIds.length}
        >
          Aperçu
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          onClick={handleDownload}
          disabled={!selectedTemplate || !canPrint || loading || previewing || !payslipIds.length}
        >
          Télécharger PDF
          {(selectedTemplateOption as any)?.is_default && ' (Par défaut)'}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <Paragraph>
          Sélectionnez le format de bulletin de paie qui correspond à vos besoins.
          Vous pouvez utiliser les templates prédéfinis ou créer vos propres templates personnalisés.
        </Paragraph>
      </div>

      {loadError && <Alert type="warning" showIcon message={loadError} description="Les formats standard restent disponibles. Réessayez pour accéder aux modèles de votre entreprise." action={<Button onClick={loadTemplates}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
      {!payslipIds.length && <Alert type="info" showIcon message="Sélectionnez un bulletin dans la liste pour consulter son aperçu ou télécharger son PDF. L’éditeur propose un exemple pour préparer vos modèles." style={{ marginBottom: 16 }} />}
      {!!payslipIds.length && !canPrint && <Alert type="info" showIcon message="Le droit d’imprimer les bulletins est nécessaire pour télécharger un PDF." style={{ marginBottom: 16 }} />}
      <Radio.Group value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)} style={{ marginBottom: 16 }}>
        <Radio value="default">Modèle par défaut de l’entreprise</Radio>
      </Radio.Group>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Chargement des modèles...</div>
        </div>
      ) : (
        <Tabs defaultActiveKey="custom" type="card">
          <TabPane
            tab={
              <span>
                <BuildOutlined />
                Modèles personnalisés
                <Badge count={templates.length} size="small" style={{ marginLeft: 4 }} />
              </span>
            }
            key="custom"
          >
            {templates.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Aucun modèle personnalisé"
                style={{ padding: '40px 0' }}
              >
                <Button type="primary" icon={<PlusOutlined />} disabled={!canCreate} onClick={handleCreateTemplate}>
                  Créer votre premier modèle
                </Button>
              </Empty>
            ) : (
              <Radio.Group
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full"
              >
                <Row gutter={[16, 16]}>
                  {templates.map((template) => {
                    const primaryColor = template.style_config.colors.primary;
                    return (
                      <Col xs={24} sm={12} lg={8} key={template.id}>
                        <Card
                          hoverable
                          className={`h-full transition-all duration-200 ${
                            selectedTemplate === template.id.toString()
                              ? 'shadow-lg border-primary'
                              : 'hover:shadow-md'
                          }`}
                          style={{
                            borderColor: selectedTemplate === template.id.toString() ? primaryColor : undefined,
                            borderWidth: selectedTemplate === template.id.toString() ? '2px' : '1px',
                          }}
                          styles={{ body: { padding: '16px' } }}
                          onClick={() => setSelectedTemplate(template.id.toString())}
                          actions={[
                            ...(!template.is_global && canEdit ? [
                              <Tooltip title="Modifier le modèle" key="edit">
                                <Button type="text" icon={<SettingOutlined />} aria-label={`Modifier ${template.name}`} onClick={(e) => { e.stopPropagation(); handleEditTemplate(template.id); }} />
                              </Tooltip>
                            ] : []),
                            ...(canCopy ? [
                              <Tooltip title="Copier dans votre entreprise" key="copy">
                                <Button type="text" icon={<CopyOutlined />} aria-label={`Copier ${template.name}`} onClick={(e) => { e.stopPropagation(); setCopyTemplate(template); setCopyName(`${template.name} (copie)`); }} />
                              </Tooltip>
                            ] : [])
                          ]}
                        >
                          <Radio value={template.id.toString()} className="w-full">
                            <div className="ml-2">
                              <div className="flex items-center mb-2 justify-between">
                                <div className="flex items-center">
                                  <span
                                    style={{ color: primaryColor, fontSize: '20px' }}
                                    className="mr-2"
                                  >
                                    <FileTextOutlined />
                                  </span>
                                  <div>
                                    <Title level={5} className="!mb-0 !text-sm">
                                      {template.name}
                                      {template.is_default && (
                                        <Tooltip title="Template par défaut">
                                          <StarFilled
                                            style={{
                                              marginLeft: 8,
                                              color: themeToken.colorWarning,
                                              fontSize: '14px'
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                      {template.is_global && (
                                        <Tooltip title="Template global">
                                          <GlobalOutlined
                                            style={{
                                              marginLeft: 4,
                                              color: themeToken.colorPrimary,
                                              fontSize: '12px'
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Title>
                                  </div>
                                </div>
                                <Tag color={primaryColor}>
                                  {template.template_type}
                                </Tag>
                              </div>

                              <Paragraph
                                type="secondary" style={{ fontSize: 12, marginBottom: 12, lineHeight: 1.4 }}
                              >
                                {template.description || 'Template personnalisé'}
                              </Paragraph>

                              <div className="space-y-1">
                                <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: themeToken.colorTextSecondary }}>
                                  <div
                                    className="w-1 h-1 rounded-full mr-2"
                                    style={{ backgroundColor: primaryColor }}
                                  />
                                  {Object.values(template.elements_config).filter(config => config.visible).length} éléments visibles
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: themeToken.colorTextSecondary }}>
                                  <div
                                    className="w-1 h-1 rounded-full mr-2"
                                    style={{ backgroundColor: primaryColor }}
                                  />
                                  Couleurs personnalisées
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: themeToken.colorTextSecondary }}>
                                  <div
                                    className="w-1 h-1 rounded-full mr-2"
                                    style={{ backgroundColor: primaryColor }}
                                  />
                                  Police: {template.style_config.fonts.family}
                                </div>
                              </div>
                            </div>
                          </Radio>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Radio.Group>
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <GlobalOutlined />
                Formats standard
                <Badge count={staticTemplateOptions.length} size="small" style={{ marginLeft: 4 }} />
              </span>
            }
            key="standard"
          >
            <Radio.Group
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full"
            >
              <Row gutter={[16, 16]}>
                {staticTemplateOptions.map((template) => (
                  <Col xs={24} sm={12} lg={8} key={template.key}>
                    <Card
                      hoverable
                      className={`h-full transition-all duration-200 ${
                        selectedTemplate === template.key
                          ? 'shadow-lg border-primary'
                          : 'hover:shadow-md'
                      }`}
                      style={{
                        borderColor: selectedTemplate === template.key ? template.color : undefined,
                        borderWidth: selectedTemplate === template.key ? '2px' : '1px',
                      }}
                      styles={{ body: { padding: '16px' } }}
                      onClick={() => setSelectedTemplate(template.key)}
                    >
                      <Radio value={template.key} className="w-full">
                        <div className="ml-2">
                          <div className="flex items-center mb-2">
                            <span
                              style={{ color: template.color, fontSize: '20px' }}
                              className="mr-2"
                            >
                              {template.icon}
                            </span>
                            <div>
                              <Title level={5} className="!mb-0 !text-sm">
                                {template.name}
                                {template.recommended && (
                                  <Tooltip title="Format complet recommandé">
                                    <span
                                      className="ml-2 px-2 py-0.5 text-xs rounded"
                                      style={{
                                        backgroundColor: template.color + '20',
                                        color: template.color,
                                        border: `1px solid ${template.color}40`
                                      }}
                                    >
                                      Recommandé
                                    </span>
                                  </Tooltip>
                                )}
                              </Title>
                            </div>
                          </div>

                          <Paragraph
                            type="secondary" style={{ fontSize: 12, marginBottom: 12, lineHeight: 1.4 }}
                          >
                            {template.description}
                          </Paragraph>

                          <div className="space-y-1">
                            {template.features.map((feature, index) => (
                              <div key={index} style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: themeToken.colorTextSecondary }}>
                                <div
                                  className="w-1 h-1 rounded-full mr-2"
                                  style={{ backgroundColor: template.color }}
                                />
                                {feature}
                              </div>
                            ))}
                          </div>
                        </div>
                      </Radio>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </TabPane>
        </Tabs>
      )}

      {selectedTemplateOption && (
        <Card
          className="mt-4 border-l-4"
          style={{
            borderLeftColor: (selectedTemplateOption as any).style_config?.colors?.primary || (selectedTemplateOption as any).color
          }}
          styles={{ body: { padding: '12px 16px' } }}
        >
          <div className="flex items-center">
            <span
              style={{
                color: (selectedTemplateOption as any).style_config?.colors?.primary || (selectedTemplateOption as any).color,
                fontSize: '16px'
              }}
              className="mr-2"
            >
              {(selectedTemplateOption as any).icon || <FileTextOutlined />}
            </span>
            <div>
              <Text strong style={{
                color: themeToken.colorText
              }}>
                {selectedTemplateOption.name} sélectionné
              </Text>
              <br />
              <Text type="secondary" className="text-xs">
                {(selectedTemplateOption as any).description || 'Template personnalisé'}
              </Text>
            </div>
          </div>
        </Card>
      )}

      <Alert type="info" showIcon message="Personnalisation du bulletin" description="La présentation choisie conserve les totaux de la paie enregistrée. Le détail fiscal s’affiche lorsqu’il existe dans le calcul sauvegardé. Vérifiez les mentions adaptées à votre entreprise." style={{ marginTop: 16 }} />
      <Modal title="Copier le modèle dans votre entreprise" open={!!copyTemplate && visible} onCancel={() => !copying && setCopyTemplate(null)} okText="Copier le modèle" cancelText="Annuler" confirmLoading={copying} okButtonProps={{ disabled: !copyName.trim() }} onOk={handleCopy}>
        <Text>Nom du nouveau modèle</Text>
        <Input aria-label="Nom du nouveau modèle" value={copyName} maxLength={100} onChange={(event) => setCopyName(event.target.value)} style={{ marginTop: 8 }} />
      </Modal>
      <Modal title="Aperçu du bulletin" open={previewOpen && visible} onCancel={() => setPreviewOpen(false)} footer={null} width={1000}>
        {payslipIds.length > 1 && <Alert type="info" showIcon message="L’aperçu porte sur le premier bulletin de la sélection." style={{ marginBottom: 16 }} />}
        <iframe title="Aperçu du bulletin de paie" sandbox="" referrerPolicy="no-referrer" srcDoc={previewHTML} style={{ width: '100%', height: '65vh', border: `1px solid ${themeToken.colorBorder}`, background: '#fff' }} />
      </Modal>

      {/* Template Builder Modal */}
      <PayslipTemplateBuilder
        visible={showTemplateBuilder}
        onCancel={() => setShowTemplateBuilder(false)}
        templateId={editingTemplateId}
        onSave={handleTemplateSaved}
      />
    </Modal>
  );
};

export default TemplateSelector;
